import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Search, Filter, Eye, Truck } from "lucide-react";
import { api } from "../../../lib/auth";

const STATUSES = ["all", "pending", "processing", "shipped", "delivered", "cancelled"];
const STATUS_COLORS = {
  pending: "bg-amber-100 text-amber-700",
  processing: "bg-blue-100 text-blue-700",
  shipped: "bg-violet-100 text-violet-700",
  delivered: "bg-emerald-100 text-emerald-700",
  cancelled: "bg-rose-100 text-rose-700",
};

export default function StoreOrders() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);

  const load = async () => {
    const r = await api.get("/t/store/orders", { params: { status: filter } });
    setRows(r.data);
  };
  useEffect(() => { load(); }, [filter]); // eslint-disable-line

  const setStatus = async (oid, status) => {
    try {
      await api.patch(`/t/store/orders/${oid}/status`, null, { params: { status } });
      toast.success("Order updated");
      load();
      setSelected(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const filtered = rows.filter((o) =>
    !search ||
    o.order_number?.toLowerCase().includes(search.toLowerCase()) ||
    o.customer_name?.toLowerCase().includes(search.toLowerCase()) ||
    o.customer_email?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div data-testid="store-orders-page" className="p-6 lg:p-8">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
        <div>
          <h1 className="font-display font-extrabold text-3xl text-black tracking-tight">Online Orders</h1>
          <div className="text-sm text-black/55 mt-1">Store · Online Orders</div>
        </div>
      </div>

      <div className="flex items-center gap-3 mb-5 flex-wrap">
        <div className="flex items-center gap-2 bg-white rounded-xl border border-black/10 px-3 py-2 flex-1 min-w-[260px] max-w-md">
          <Search size={16} className="text-black/40" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search orders…"
            data-testid="orders-search"
            className="flex-1 bg-transparent focus:outline-none text-sm" />
        </div>
        <div className="flex gap-1.5 flex-wrap">
          {STATUSES.map((s) => (
            <button key={s} onClick={() => setFilter(s)} data-testid={`orders-filter-${s}`}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold capitalize ${
                filter === s ? "bg-[#1FA868] text-white" : "bg-white border border-black/10 text-black/65 hover:border-black/25"
              }`}>{s}</button>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-black/5 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#FAFBFD] text-black/55 text-[11px] uppercase tracking-wider">
            <tr>
              <th className="text-left p-3.5 font-bold">Order</th>
              <th className="text-left p-3.5 font-bold">Customer</th>
              <th className="text-left p-3.5 font-bold">Items</th>
              <th className="text-left p-3.5 font-bold">Total</th>
              <th className="text-left p-3.5 font-bold">Status</th>
              <th className="text-left p-3.5 font-bold">Date</th>
              <th className="text-right p-3.5 font-bold">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-black/5">
            {filtered.map((o) => (
              <tr key={o.id} className="hover:bg-[#FAFBFD]">
                <td className="p-3.5 font-mono text-xs text-black">{o.order_number}</td>
                <td className="p-3.5">
                  <div className="font-semibold text-black text-sm">{o.customer_name}</div>
                  <div className="text-[11px] text-black/50">{o.customer_email}</div>
                </td>
                <td className="p-3.5 text-black/70">{(o.items || []).length} items</td>
                <td className="p-3.5 font-bold text-black tabular-nums">₦{Number(o.total || 0).toLocaleString()}</td>
                <td className="p-3.5">
                  <span className={`text-[10px] font-bold uppercase px-2 py-1 rounded-md ${STATUS_COLORS[o.status] || "bg-black/5 text-black/60"}`}>{o.status}</span>
                </td>
                <td className="p-3.5 text-black/55 text-xs">{(o.created_at || "").slice(0, 10)}</td>
                <td className="p-3.5 text-right">
                  <button onClick={() => setSelected(o)} data-testid={`order-view-${o.order_number}`}
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-white border border-black/10 text-xs font-semibold hover:border-black/25">
                    <Eye size={12} /> View
                  </button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan="7" className="p-10 text-center text-black/45">No orders.</td></tr>}
          </tbody>
        </table>
      </div>

      {selected && (
        <div className="fixed inset-0 bg-black/40 z-50 flex justify-end" onClick={() => setSelected(null)}>
          <div className="w-full max-w-lg bg-white h-full overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-black/8 flex items-center justify-between">
              <h2 className="font-display font-bold text-xl text-black">{selected.order_number}</h2>
              <button onClick={() => setSelected(null)} className="p-2 rounded-lg hover:bg-black/5">×</button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <div className="text-[10px] uppercase font-bold text-black/55">Customer</div>
                <div className="font-semibold text-black">{selected.customer_name}</div>
                <div className="text-xs text-black/55">{selected.customer_email} · {selected.customer_phone}</div>
                <div className="text-xs text-black/55 mt-1">{selected.shipping_address}</div>
              </div>

              <div>
                <div className="text-[10px] uppercase font-bold text-black/55 mb-2">Items</div>
                <div className="space-y-1.5">
                  {(selected.items || []).map((it, i) => (
                    <div key={i} className="flex items-center justify-between text-sm border-b border-black/5 pb-1.5">
                      <div>{it.name} <span className="text-black/45">× {it.qty}</span></div>
                      <div className="font-bold tabular-nums">₦{Number(it.price * it.qty).toLocaleString()}</div>
                    </div>
                  ))}
                </div>
                <div className="mt-3 flex justify-between items-center text-base font-bold">
                  <span>Total</span>
                  <span>₦{Number(selected.total || 0).toLocaleString()}</span>
                </div>
              </div>

              <div>
                <div className="text-[10px] uppercase font-bold text-black/55 mb-2">Update status</div>
                <div className="grid grid-cols-2 gap-2">
                  {["processing", "shipped", "delivered", "cancelled"].map((st) => (
                    <button key={st} onClick={() => setStatus(selected.id, st)} data-testid={`order-set-${st}`}
                      className={`py-2 rounded-lg text-xs font-semibold capitalize ${STATUS_COLORS[st]} hover:opacity-90`}>
                      <Truck size={12} className="inline mr-1" /> {st}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
